import type { CoachingRole } from "../domain/roles";

export type RouteCapability =
  | "auth:view"
  | "dashboard:view"
  | "companies:view"
  | "programs:view"
  | "cohorts:view"
  | "cohorts:create"
  | "participants:view"
  | "sessions:view"
  | "sessions:create"
  | "sessions:invite"
  | "sessions:reschedule"
  | "reports:view"
  | "analytics:view"
  | "resources:view"
  | "capstone:view"
  | "certificates:view"
  | "history:view"
  | "users:view"
  | "permissions:view"
  | "exports:view"
  | "settings:view"
  | "tools:view";

export type RouteReadiness = "foundation" | "planned-screen" | "blocked-backend";
export type RouteScopeKind =
  | "none"
  | "company"
  | "program"
  | "cohort"
  | "participant"
  | "session"
  | "report"
  | "resource"
  | "user";

export interface AppRouteDefinition {
  id: string;
  path: string;
  label: string;
  capability: RouteCapability;
  roles: CoachingRole[];
  directUrlPolicy: "allow" | "generic-not-found";
  nav: boolean;
  platformAdminOnly?: boolean;
  readiness?: RouteReadiness;
  scopeKind?: RouteScopeKind;
  scopeParam?: string;
  featureGate?: string;
}

export const allRoles: CoachingRole[] = ["super_admin", "admin", "coach", "company_admin", "participant", "cohort_lead"];
export const operationsRoles: CoachingRole[] = ["super_admin", "admin", "coach", "company_admin"];
export const writeOperationsRoles: CoachingRole[] = ["super_admin", "admin", "coach"];
export const adminRoles: CoachingRole[] = ["super_admin", "admin"];
// HR4 nav-truth: coach's cohort entry point is My Cohorts. Keep the
// operational Cohorts directory for admin/read-admin surfaces only.
export const cohortDirectoryRoles: CoachingRole[] = ["super_admin", "admin", "company_admin", "cohort_lead"];
// F7 cohort lead: assigned-cohort VIEW + report-download. Added to the cohort
// workspace and reports read routes so a lead can inspect its cohorts and pull
// reports, while the capability layer keeps every mutation denied.
export const cohortReadRoles: CoachingRole[] = ["super_admin", "admin", "coach", "company_admin", "cohort_lead"];

// ── Resources ADMIN module allow-list (Wave 8 — participant exclusion) ───────
// The ADMIN Resources module (browse/manage/attach + the upload/download
// affordances) is for STAFF / coach / company_admin / cohort_lead ONLY.
// PARTICIPANT is DENIED the admin module at the ROUTE (generic-not-found + no
// nav) AND at the page (canAccessResourcesModule fail-closed). A participant's
// own resources context is the Wave-9 participant dashboard, NOT this admin
// surface. Mutating this back to allRoles must fail the participant-denied
// biting tests. (NOTE: this REPLACES the prior allRoles posture on /resources.)
export const resourceModuleRoles: CoachingRole[] = ["super_admin", "admin", "coach", "company_admin", "cohort_lead"];

// ── Capstone ADMIN module allow-list (Wave 8 — STAFF/coach only) ─────────────
// The capstone ADMIN view (submissions list/detail + the upload/conversion/
// evaluation/certificate-trigger statuses) follows the only backend capstone
// route (POST /cohorts/:id/capstone-submissions, manage.sessions/global =
// STAFF). PARTICIPANT is DENIED (route + page fail-closed); the participant
// capstone-submit surface is a Wave-9 dependency on a participant-scoped submit
// route that does NOT exist yet (backend gap). cohort_lead/company_admin are
// read-only observers of their scoped cohort submissions.
export const capstoneModuleRoles: CoachingRole[] = ["super_admin", "admin", "coach", "company_admin", "cohort_lead"];

// ── Reports route allow-list (Wave 7 — FR-REPORT-001 participant exclusion) ──
// PARTICIPANT GENERATED-REPORT EXCLUSION is release-blocking: a participant has
// ZERO visibility of the generated-report taxonomy (team_admin / coach_feedback /
// vjal_internal / ceo_confidential) across EVERY surface — route, nav, list,
// detail, download, status, failure detail. The four-type Reports module is for
// STAFF / coach / company_admin / cohort_lead ONLY. Removing participant from the
// reports route roles makes the route the FIRST fail-closed layer (generic-not-
// found on a direct URL + no nav); the report pages ALSO fail-close belt-and-
// suspenders (canAccessReportsModule). This list is the route-level half of the
// gate; mutating it back to allRoles must fail the report-exclusion biting tests.
export const reportViewRoles: CoachingRole[] = ["super_admin", "admin", "coach", "company_admin", "cohort_lead"];
